import { useState } from "react";
import type { ApiError } from "@/lib/api";
import { errorMessage } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import type { CoachingNote, CoachingPlaylist } from "@/lib/types";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";
import { CoachModal } from "./CoachModal";
import { CheckList } from "./CheckList";
import { useCoachNotesList } from "../hooks/useCoachNotesList";
import { useRoster } from "../hooks/useRoster";
import type { CoachPlaylistMutations } from "../hooks/useCoachPlaylistsList";
import { TkticSelect } from "@/components/ui/TkticSelect";

/**
 * Playlist composer modal — Coach > Playlists.
 *
 * Ports the legacy `openCoachPlaylistModal`
 * (`js/coaching/playlists.js`). A `null` `playlist` prop ⇒ create;
 * a `CoachingPlaylist` ⇒ edit.
 *
 * Fields mirror `CreateCoachingPlaylistRequest`:
 *   - title (required), description, visibility
 *   - note_ids — ordered item list, picked from a checklist of
 *     the team's notes
 *   - player_ids — playlist-level player tags, picked from a
 *     checklist of the roster
 *   - pre_roll_seconds / post_roll_seconds — the session
 *     windowing defaults the M5 playlist controller reads
 *
 * Unlike the Notes/Clips composers (which live in Coach Review),
 * the playlist composer has no Coach Review surface — it's its
 * own modal, NEW in M6a.
 */

const VISIBILITY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "private", label: "Coach-only (private)" },
  { value: "team", label: "Team" },
  { value: "player", label: "Player + family" },
  { value: "unlisted", label: "Unlisted" },
];

export type PlaylistComposerModalProps = {
  /** `null` ⇒ create; a `CoachingPlaylist` ⇒ edit it. */
  playlist: CoachingPlaylist | null;
  mutations: CoachPlaylistMutations;
  onClose: () => void;
  onSaved?: () => void;
};

export function PlaylistComposerModal({
  playlist,
  mutations,
  onClose,
  onSaved,
}: PlaylistComposerModalProps) {
  const isEdit = playlist !== null;
  const toast = useToast();
  const notesQuery = useCoachNotesList();
  const rosterQuery = useRoster();

  const [title, setTitle] = useState(playlist?.title ?? "");
  const [description, setDescription] = useState(playlist?.description ?? "");
  // Typed as plain `string` — `CreateCoachingPlaylistRequest.
  // visibility` is a string, not the narrow `CoachingVisibility`
  // literal, so the `<select>`'s onChange (string) assigns cleanly.
  const [visibility, setVisibility] = useState<string>(
    playlist?.visibility ?? "private",
  );
  const [preRoll, setPreRoll] = useState(playlist?.pre_roll_seconds ?? 5);
  const [postRoll, setPostRoll] = useState(playlist?.post_roll_seconds ?? 8);
  // Selected note + player ids — seeded from the playlist on edit.
  const [noteIds, setNoteIds] = useState<Set<number>>(
    () => new Set(playlist?.note_ids ?? []),
  );
  const [playerIds, setPlayerIds] = useState<Set<string>>(
    () => new Set(playlist?.player_ids ?? []),
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Only video notes are playable in a playlist session: the M5
  // `usePlaylistSession` advance monitor reads `video.currentTime`,
  // and an observation note (`note_context === "observation"`) has
  // no `match_id` to load a video from — it would stall the
  // session. Filter the picker to video notes so a coach can't
  // build an unplayable playlist. (Teaching the session to render
  // observation-only items is a later feature, not M6a.)
  const videoNotes = (notesQuery.data?.notes ?? []).filter(
    (n) => n.note_context !== "observation",
  );
  const players = rosterQuery.data?.players ?? [];

  // An EDITED playlist could already carry observation note ids
  // saved before this filter existed; keep showing those rows
  // (selected) so the coach can see — and remove — them, rather
  // than silently hiding a selected item they can't unpick.
  const selectedObservationNotes = (notesQuery.data?.notes ?? []).filter(
    (n) => n.note_context === "observation" && noteIds.has(n.id),
  );
  const pickerNotes = [...videoNotes, ...selectedObservationNotes];

  // Save is BLOCKED while any selected item is an observation note:
  // the picker can't add new ones, but an edited playlist could
  // still carry pre-existing observation ids, and submitting them
  // unchanged would re-save an unplayable item. Block (not silently
  // filter) so the coach gets a clear, actionable message instead
  // of losing an item they can see selected.
  const selectedObservationIds = selectedObservationNotes.map((n) => n.id);
  const hasUnplayableItems = selectedObservationIds.length > 0;

  const toggleNote = (id: number) =>
    setNoteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const togglePlayer = (id: string) =>
    setPlayerIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    if (!t) {
      setError("Review session title is required.");
      return;
    }
    // Block save while a non-playable observation item is still
    // selected — submitting `note_ids` unchanged would re-save it.
    if (hasUnplayableItems) {
      setError(
        `Remove the observation note${
          selectedObservationIds.length > 1 ? "s" : ""
        } before saving — review sessions only play video notes.`,
      );
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const body = {
        title: t,
        description: description.trim(),
        visibility,
        // Preserve the existing item ORDER on edit — the playlist
        // already has `note_ids` in order; keep that ordering and
        // append any newly-checked ids. (Drag-reorder is a polish
        // follow-up; the legacy modal didn't reorder either.)
        note_ids: orderedNoteIds(playlist, noteIds),
        player_ids: [...playerIds],
        pre_roll_seconds: Number(preRoll) || 5,
        post_roll_seconds: Number(postRoll) || 8,
      };
      if (isEdit) {
        await mutations.updatePlaylist(playlist.id, body);
      } else {
        await mutations.createPlaylist(body);
      }
      toast.success(
        isEdit ? "Review session updated" : "Review session created",
      );
      onSaved?.();
      onClose();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(
        typeof apiErr?.detail === "string"
          ? apiErr.detail
          : `Could not ${isEdit ? "update" : "create"} the review session.`,
      );
      toast.error(
        `Couldn't ${isEdit ? "update" : "create"} review session`,
        { description: errorMessage(err) },
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CoachModal
      title={isEdit ? "Edit review session" : "New review session"}
      onClose={onClose}
      data-testid="playlist-composer-modal"
    >
      <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
        <Field label="Title">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="playlist-composer-title"
            autoFocus
            className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </Field>

        <Field label="Description">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="playlist-composer-description"
            rows={2}
            className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </Field>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Visibility">
            <TkticSelect
              value={visibility}
              onChange={(next) => setVisibility(next)}
              data-testid="playlist-composer-visibility"
              ariaLabel="Visibility"
              options={VISIBILITY_OPTIONS.map((v) => ({
                value: v.value,
                label: v.label,
              }))}
            />
          </Field>
          <Field label="Pre-roll (s)">
            <input
              type="number"
              min="0"
              value={preRoll}
              onChange={(e) => setPreRoll(Number(e.target.value))}
              data-testid="playlist-composer-preroll"
              className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </Field>
          <Field label="Post-roll (s)">
            <input
              type="number"
              min="0"
              value={postRoll}
              onChange={(e) => setPostRoll(Number(e.target.value))}
              data-testid="playlist-composer-postroll"
              className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </Field>
        </div>

        {/* Note item picker — a checklist of the team's VIDEO notes
         * (observation notes aren't playable in a session). */}
        <Field
          label={`Notes (${noteIds.size} selected)`}
          helper="Video notes only — the ordered items the session plays back."
        >
          <CheckList
            data-testid="playlist-composer-notes"
            emptyLabel="No video notes yet"
            items={pickerNotes.map((n) => ({
              value: n.id,
              label: noteLabel(n),
              selected: noteIds.has(n.id),
            }))}
            onToggle={(v) => toggleNote(v as number)}
          />
        </Field>

        {/* Player tags — a checklist of the roster. */}
        <Field label={`Tag players (${playerIds.size} selected)`}>
          <CheckList
            data-testid="playlist-composer-players"
            emptyLabel="No players yet"
            items={players.map((p) => ({
              value: p.id,
              label: p.jersey_number
                ? `#${p.jersey_number} ${p.display_name}`
                : p.display_name,
              selected: playerIds.has(p.id),
            }))}
            onToggle={(v) => togglePlayer(v as string)}
          />
        </Field>

        {/* Persistent warning when an edited playlist still carries
         * a non-playable observation item — explains the disabled
         * Save before the coach clicks it. */}
        {hasUnplayableItems ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="playlist-composer-unplayable-warning"
          >
            {selectedObservationIds.length} observation note
            {selectedObservationIds.length > 1 ? "s are" : " is"} still
            selected. Uncheck {selectedObservationIds.length > 1 ? "them" : "it"}{" "}
            — review sessions only play video notes.
          </p>
        ) : null}

        {error ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="playlist-composer-error"
          >
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting || hasUnplayableItems}
          data-testid="playlist-composer-submit"
          className="bg-accent hover:bg-accent-hover disabled:bg-bg-card disabled:text-ink-muted text-white rounded px-4 py-2 text-sm font-display uppercase tracking-wide transition-colors"
        >
          {submitting
            ? "Saving…"
            : isEdit
              ? "Save changes"
              : "Create session"}
        </button>
      </form>
    </CoachModal>
  );
}

/** Preserve the playlist's existing item order, then append any
 * newly-checked note ids (and drop unchecked ones). Drag-reorder
 * is a polish follow-up — the legacy modal didn't reorder either. */
function orderedNoteIds(
  playlist: CoachingPlaylist | null,
  selected: Set<number>,
): number[] {
  const existingOrder = playlist?.note_ids ?? [];
  const kept = existingOrder.filter((id) => selected.has(id));
  const appended = [...selected].filter((id) => !existingOrder.includes(id));
  return [...kept, ...appended];
}

/** A note's display label for the checklist. Observation notes
 * only appear here when an edited playlist already had one
 * selected — they're flagged "not playable" so the coach knows
 * to remove them. */
function noteLabel(n: CoachingNote): string {
  const title =
    n.title?.trim() ||
    n.event_title?.trim() ||
    (n.note_context === "observation" ? "Observation" : "(untitled)");
  if (n.note_context === "observation") {
    const date = n.event_date ? ` · ${n.event_date}` : "";
    return `${title}${date} · not playable — remove`;
  }
  return `${title} · ${formatPlayhead(n.timestamp_seconds)}`;
}

function Field(props: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
      </span>
      {props.children}
      {props.helper ? (
        <span className="text-xs text-ink-muted">{props.helper}</span>
      ) : null}
    </label>
  );
}
